import type { Html, Link, Nodes, Parent, RootContent, Text } from 'mdast'
import { visit } from 'unist-util-visit'

import { expandCitationIds } from '@/components/chat-v2/lib/citations'

import { isHttpUrl } from '@/helpers/url'

// Map node types to their type strings
type NodeTypeMap = {
  text: Text
  link: Link
  html: Html
}

// Helper function to create remark plugins with consistent structure
const createRemarkPlugin =
  <K extends keyof NodeTypeMap>(
    nodeType: K,
    visitor: (node: NodeTypeMap[K], index: number, parent: Parent) => void,
  ) =>
  () =>
  (tree: Nodes) => {
    visit(
      tree,
      nodeType,
      (node: Nodes, index: number | undefined, parent: Parent | undefined) => {
        if (!parent || index === undefined) return
        visitor(node as NodeTypeMap[K], index, parent)
      },
    )
  }

type CitationRemarkPluginOptions = {
  pattern: RegExp
  transformText?: (value: string, matches: RegExpMatchArray[]) => RootContent[]
  transformHtml?: (value: string) => string
}

const createCitationRemarkPlugin =
  ({ pattern, transformText, transformHtml }: CitationRemarkPluginOptions) =>
  () =>
  (tree: Nodes) => {
    visit(tree, ['html', 'text'], (node, index, parent) => {
      if (!parent || index === undefined) return

      if (node.type === 'html' && transformHtml) {
        pattern.lastIndex = 0
        if (!pattern.test(node.value)) return
        pattern.lastIndex = 0
        const newValue = transformHtml(node.value)
        if (newValue !== node.value) {
          node.value = newValue
        }
        return
      }

      if (node.type === 'text' && transformText) {
        pattern.lastIndex = 0
        const matches = [...node.value.matchAll(pattern)]
        if (matches.length === 0) return
        const children = transformText(node.value, matches)
        parent.children.splice(index, 1, ...children)
      }
    })
  }

// Remark plugin to convert [1] style citations to custom HTML elements
// Groups consecutive citations together to match new format rendering style
export const remarkNumberedCitations = createCitationRemarkPlugin({
  pattern: /\[(\d+)\]/g,
  transformText: (value, matches) => {
    const children: RootContent[] = []
    let lastIndex = 0
    let citationGroup: string[] = []

    const flushCitationGroup = () => {
      if (citationGroup.length === 0) return

      if (citationGroup.length === 1) {
        // Single citation - use data-id
        children.push({
          type: 'html',
          value: `<source-citation data-id="${citationGroup[0]}"></source-citation>`,
        } as Html)
      } else {
        // Multiple citations - use data-ids (same as new format)
        children.push({
          type: 'html',
          value: `<source-citation data-ids="${citationGroup.join(',')}"></source-citation>`,
        } as Html)
      }

      citationGroup = []
    }

    matches.forEach((match, matchIndex) => {
      const [fullMatch, sourceId] = match
      const startIndex = match.index ?? 0
      const prevMatch = matchIndex > 0 ? matches[matchIndex - 1] : null
      const prevEndIndex = prevMatch
        ? (prevMatch.index ?? 0) + prevMatch[0].length
        : -1

      // Check if this citation is consecutive (no text between citations)
      const isConsecutive = prevMatch !== null && startIndex === prevEndIndex

      if (isConsecutive && citationGroup.length > 0) {
        // Add to existing group
        citationGroup.push(sourceId)
        // Update lastIndex to include this citation
        lastIndex = startIndex + fullMatch.length
      } else {
        // Flush previous group if exists
        flushCitationGroup()

        // Add text before the match
        if (startIndex > lastIndex) {
          children.push({
            type: 'text',
            value: value.slice(lastIndex, startIndex),
          } as Text)
        }

        // Start new group
        citationGroup = [sourceId]
        lastIndex = startIndex + fullMatch.length
      }
    })

    // Flush any remaining group
    flushCitationGroup()

    // Add any remaining text
    if (lastIndex < value.length) {
      children.push({
        type: 'text',
        value: value.slice(lastIndex),
      } as Text)
    }

    return children
  },
})

// Remark plugin to convert [source_website](link) style citations to custom HTML elements
export const remarkWebLinks = createRemarkPlugin(
  'link',
  (node, index, parent) => {
    if (!node.url) return

    if (!isHttpUrl(node.url)) {
      parent.children.splice(index, 1, ...node.children)
      return
    }

    // Check if this link has children
    if (!node.children || node.children.length === 0) return

    const firstChild = node.children[0] as Text
    if (firstChild.type !== 'text') return

    const linkText = firstChild.value
    const linkUrl = node.url

    // Check if there are surrounding bracket nodes
    const prevNode = index > 0 ? parent.children[index - 1] : null
    const nextNode =
      index < parent.children.length - 1 ? parent.children[index + 1] : null

    // Check if previous node is a text node ending with '['
    if (prevNode && prevNode.type === 'text') {
      const prevText = (prevNode as Text).value
      if (prevText === '[') {
        // Remove entire bracket node
        parent.children.splice(index - 1, 1)
        index-- // Adjust index since we removed a node before
      } else if (prevText.endsWith('[')) {
        // Update the previous node to remove trailing bracket
        ;(prevNode as Text).value = prevText.slice(0, -1)
      }
    }

    // Check if next node is a text node starting with ']'
    if (nextNode && nextNode.type === 'text') {
      const nextText = (nextNode as Text).value
      if (nextText === ']') {
        // Remove entire bracket node (will be done after replacing link)
        parent.children.splice(index + 1, 1)
      } else if (nextText.startsWith(']')) {
        // Update the next node to remove leading bracket
        ;(nextNode as Text).value = nextText.slice(1)
      }
    }

    // Create custom HTML element for web link
    const linkElement: Html = {
      type: 'html',
      value: `<web-link data-url="${linkUrl}">${linkText}</web-link>`,
    }

    // Replace the link node with the custom element
    parent.children.splice(index, 1, linkElement)
  },
)

// Remark plugin to parse <cite id="1" url="..." /> tags.
// Content-bearing citations are normalized to `content <citation>` so the
// cited text stays visible and the marker remains interactive.
const renderCitationElement = (attributes: string) => {
  const idMatch = attributes.match(/id=["']([^"']*)["']/)
  const urlMatch = attributes.match(/url=["']([^"']*)["']/)
  const idString = idMatch ? idMatch[1] : ''
  const url = urlMatch ? urlMatch[1] : ''

  if (url && isHttpUrl(url)) {
    return `<web-citation data-id="${idString}" data-url="${url}"></web-citation>`
  }

  const expandedIds = expandCitationIds(idString)
  if (expandedIds.length === 1) {
    return `<source-citation data-id="${expandedIds[0]}"></source-citation>`
  }
  return `<source-citation data-ids="${expandedIds.join(',')}"></source-citation>`
}

const openingCitePattern = /^<cite\s+([^>]+?)\s*(\/?)>$/
const closingCitePattern = /^<\/cite\s*>$/

// Inline HTML tokenizes each tag into its own node (handled below), but a
// BLOCK of raw HTML — `<div>Claim <cite id="7" /></div>`, or an opener alone
// on its line — arrives as ONE html node holding the whole block, which the
// anchored patterns skip. Rewrite cite tags inside such values in place,
// keeping the content-then-marker order.
const embeddedCitePairPattern = /<cite\s+([^>]+?)\s*>([\s\S]*?)<\/cite\s*>/g
const embeddedCiteSelfClosingPattern = /<cite\s+([^>]+?)\s*\/>/g

const rewriteEmbeddedCitations = (value: string) =>
  value
    .replace(
      embeddedCitePairPattern,
      (_match, attributes: string, content: string) =>
        `${content} ${renderCitationElement(attributes)}`,
    )
    .replace(embeddedCiteSelfClosingPattern, (_match, attributes: string) =>
      renderCitationElement(attributes),
    )

export const remarkWebCitations = () => (tree: Nodes) => {
  visit(tree, (node) => {
    if (!('children' in node)) return

    const parent = node as Parent
    for (let index = 0; index < parent.children.length; index += 1) {
      const child = parent.children[index]
      if (child.type !== 'html') continue

      const openingMatch = child.value.match(openingCitePattern)
      if (!openingMatch) {
        if (child.value.includes('<cite')) {
          child.value = rewriteEmbeddedCitations(child.value)
        }
        continue
      }

      const [, attributes, selfClosingMarker] = openingMatch
      const citationElement = renderCitationElement(attributes)
      if (selfClosingMarker) {
        child.value = citationElement
        continue
      }

      const closingIndex = parent.children.findIndex(
        (candidate, candidateIndex) =>
          candidateIndex > index &&
          candidate.type === 'html' &&
          closingCitePattern.test(candidate.value),
      )

      if (closingIndex === -1) {
        child.value = citationElement
        continue
      }

      parent.children.splice(index, 1)
      parent.children[closingIndex - 1] = {
        type: 'html',
        value: citationElement,
      } as Html
    }
  })
}

// Remark plugin to normalize <table-cite id="..."/> tags so custom components render reliably.
const tableCitationPattern = /<table-cite\s+([^>]+?)\s*\/?>/g

const normalizeTableCitationTag = (match: string, attributes: string) => {
  const idMatch = attributes.match(/id=["']([^"']*)["']/)
  if (!idMatch) return match
  const id = idMatch[1]
  // Only add closing tag for self-closing matches (ends with />)
  // to avoid double-closing when input already has </table-cite>
  const isSelfClosing = /\/\s*>$/.test(match)
  return isSelfClosing
    ? `<table-cite data-id="${id}"></table-cite>`
    : `<table-cite data-id="${id}">`
}

export const remarkTableCitations = createCitationRemarkPlugin({
  pattern: tableCitationPattern,
  transformHtml: (value) =>
    value.replace(tableCitationPattern, normalizeTableCitationTag),
  transformText: (value, matches) => {
    const children: RootContent[] = []
    let lastIndex = 0

    matches.forEach((match) => {
      const [fullMatch, attributes] = match
      const startIndex = match.index ?? 0
      const endIndex = startIndex + fullMatch.length

      if (startIndex > lastIndex) {
        children.push({
          type: 'text',
          value: value.slice(lastIndex, startIndex),
        } as Text)
      }

      const normalized = normalizeTableCitationTag(fullMatch, attributes)
      children.push({
        type: 'html',
        value: normalized,
      } as Html)

      lastIndex = endIndex
    })

    if (lastIndex < value.length) {
      children.push({
        type: 'text',
        value: value.slice(lastIndex),
      } as Text)
    }

    return children
  },
})
